/**
 * Retry e aplicação de resposta batch (ACK estrito).
 */

import {
  getSettledEventIds,
  getItemsToRetry,
  getAmbiguousAckIds,
} from '../batchResponse';
import {
  commitSettledBehaviorFlushes,
  rollbackPendingBehaviorFlush,
} from '../../behavior/buffer';
import { moveToDeadLetter, getDeadLetterCount } from '../deadLetter';
import { removePendingUnloadByIds } from '../pendingUnload';
import { setLastAckedSequenceSync } from '../replay/persistentReplay';
import {
  canRetryItem,
  scheduleRetry,
  getMaxAttemptsForItem,
} from '../retry/retryPolicy';
import { transitionStatus } from './stateMachine';
import {
  recordAmbiguousAck,
  recordDuplicated,
  recordDeadLetterCount,
  recordDropped,
  recordEventPhase,
  recordRetry,
  recordRetryByType,
  recordPartitionRetry,
} from '../telemetry';
import { getQueuePartition } from './partitioning';
import { canSpendPartitionRetryBudget } from './partitionPolicies';
import { getUnifiedPressureLevel } from '../runtime/pressureModel';
import { isLeaderTab } from '../tabCoordinator';
import {
  acquireOperationContext,
  assertOperationValid,
} from '../coordination/ownershipGate';
import type { QueueItem, QueueStatus } from './types';
import {
  findQueueIndex,
  getQueueMutable,
  removeQueueItemById,
  updateQueueItemAt,
  resyncQueueIndexFromArray,
} from './queueState';
import { isQueueIndexEnabled, getQueueIndex } from './queueIndex';
import { sendBatch } from '../../utils/sendBatch';

export function pruneQueueBeforeFlush(): void {
  const queue = getQueueMutable();
  for (let i = queue.length - 1; i >= 0; i--) {
    const item = queue[i];
    if (item.status === 'dead' || item.status === 'sent') {
      queue.splice(i, 1);
      continue;
    }
    if (item.attempts >= getMaxAttemptsForItem(item)) {
      moveToDeadLetter(item.id, item.params, item.type, item.attempts, 'max_attempts');
      queue.splice(i, 1);
    }
  }
  resyncQueueIndexFromArray();
}

export function getEligibleForFlush(now: number): QueueItem[] {
  if (isQueueIndexEnabled()) {
    const idx = getQueueIndex();
    const queued = idx.filterByStatusAndRetryReady('queued', now);
    const failed = idx.filterByStatusAndRetryReady('failed', now);
    return [...queued, ...failed];
  }
  return getQueueMutable().filter(
    i =>
      (i.status === 'queued' || i.status === 'failed') && i.nextRetryAt <= now
  );
}

export function applyBatchResponse(
  batchItems: QueueItem[],
  response: Awaited<ReturnType<typeof sendBatch>>,
  now: number
): void {
  const queue = getQueueMutable();
  const batchItemIds = batchItems.map(i => i.id);
  const settled = getSettledEventIds(response, batchItemIds, true);
  const toRetry = getItemsToRetry(batchItems, response, true);
  const retryIds = new Set(toRetry.map(i => i.id));
  const ambiguous = getAmbiguousAckIds(batchItemIds, response);

  recordAmbiguousAck(ambiguous.length);
  recordDuplicated(response.duplicated_ids.length);

  commitSettledBehaviorFlushes(settled);

  for (const item of batchItems) {
    if (settled.has(item.id)) {
      setLastAckedSequenceSync(item.params.sessionId, item.sessionSequence);
      recordEventPhase(item.id, 'acknowledged');
      removeQueueItemById(item.id);
    }
  }

  for (const item of toRetry) {
    const idx = findQueueIndex(item.id);
    if (idx < 0) continue;
    const nextAttempt = item.attempts + 1;
    if (!canRetryItem(item, now) || nextAttempt >= getMaxAttemptsForItem(item)) {
      const moved = moveToDeadLetter(
        item.id,
        item.params,
        item.type,
        nextAttempt,
        ambiguous.includes(item.id) ? 'ambiguous_ack' : 'max_attempts'
      );
      if (!moved) {
        rollbackPendingBehaviorFlush(item.id);
        removeQueueItemById(item.id);
        recordDropped(1);
      } else {
        queue[idx].status = 'dead';
        removeQueueItemById(item.id);
      }
      recordDeadLetterCount(getDeadLetterCount());
      continue;
    }
    if (
      getUnifiedPressureLevel() > 0.8 &&
      !isLeaderTab() &&
      !assertOperationValid(acquireOperationContext('queue_mutate'))
    ) {
      continue;
    }
    const partition = getQueuePartition(item);
    if (!canSpendPartitionRetryBudget(partition, now)) continue;
    recordRetry();
    recordRetryByType(item.params.type);
    recordPartitionRetry(partition);
    const nextStatus: QueueStatus =
      item.status === 'processing'
        ? transitionStatus('processing', 'failed')
        : 'failed';
    const updated: QueueItem = {
      ...item,
      status: nextStatus,
      attempts: nextAttempt,
      nextRetryAt: scheduleRetry(item, now),
    };
    delete updated.processingAt;
    delete updated.processingTabId;
    updateQueueItemAt(idx, updated);
    recordEventPhase(item.id, 'failed');
  }

  removePendingUnloadByIds(settled);

  for (const item of batchItems) {
    if (settled.has(item.id) || retryIds.has(item.id)) continue;
    rollbackPendingBehaviorFlush(item.id);
    const idx = findQueueIndex(item.id);
    if (idx < 0) continue;
    const current = queue[idx];
    updateQueueItemAt(idx, {
      ...current,
      status: 'queued',
      processingAt: undefined,
      processingTabId: undefined,
    });
  }
}

export function applyNetworkFailureRetry(
  batchItems: QueueItem[],
  now: number
): void {
  for (const item of batchItems) {
    const idx = findQueueIndex(item.id);
    if (idx < 0) continue;
    const nextAttempt = item.attempts + 1;
    if (!canRetryItem(item, now) || nextAttempt >= getMaxAttemptsForItem(item)) {
      moveToDeadLetter(
        item.id,
        item.params,
        item.type,
        nextAttempt,
        'network_error'
      );
      removeQueueItemById(item.id);
      continue;
    }
    const partition = getQueuePartition(item);
    if (!canSpendPartitionRetryBudget(partition, now)) continue;
    recordRetry();
    recordRetryByType(item.params.type);
    recordPartitionRetry(partition);
    const updated: QueueItem = {
      ...item,
      status: 'failed',
      attempts: nextAttempt,
      nextRetryAt: scheduleRetry(item, now),
    };
    delete updated.processingAt;
    delete updated.processingTabId;
    updateQueueItemAt(idx, updated);
  }
}
